import { Document, Font, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { Question } from "../../../../model/questions-summary.model";
import {
    isCheckboxQuestion,
    isMultipleChoiceLike,
    MULTIPLE_CHOICE,
    MULTIPLE_CHOICE_NO_ANSWER,
} from "../../../../shared/question-type.util";

// Register Sarabun (Thai) so the questionnaire renders Thai glyphs correctly.
// Pinned to the official Google Fonts repo on GitHub raw so the URL is stable
// independent of Google Fonts' CSS API. If these ever break, drop the .ttf
// files into /admin/src/assets/fonts and switch `src` to a local import.
Font.register({
    family: "Sarabun",
    fonts: [
        { src: "https://raw.githubusercontent.com/google/fonts/main/ofl/sarabun/Sarabun-Regular.ttf", fontWeight: "normal" },
        { src: "https://raw.githubusercontent.com/google/fonts/main/ofl/sarabun/Sarabun-Bold.ttf", fontWeight: "bold" },
    ],
});

const PALETTE = [
    "#4A25E1", "#0ea5e9", "#10b981", "#f59e0b",
    "#ef4444", "#8b5cf6", "#14b8a6", "#ec4899",
];

const styles = StyleSheet.create({
    page: {
        padding: 36,
        fontFamily: "Sarabun",
        fontSize: 10,
        color: "#1f2937",
        backgroundColor: "#ffffff",
        lineHeight: 1.5,
    },
    coverHeader: {
        marginBottom: 16,
        borderBottomWidth: 2,
        borderBottomColor: "#4A25E1",
        paddingBottom: 8,
    },
    coverTitle: {
        fontSize: 18,
        fontWeight: "bold",
        color: "#111827",
    },
    coverSubtitle: {
        fontSize: 10,
        color: "#6b7280",
        marginTop: 2,
    },
    summaryGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginBottom: 18,
        marginHorizontal: -4,
    },
    summaryCard: {
        width: "50%",
        padding: 4,
    },
    summaryCardInner: {
        backgroundColor: "#f5f3ff",
        borderRadius: 6,
        padding: 10,
    },
    summaryLabel: {
        fontSize: 9,
        color: "#6b7280",
    },
    summaryValue: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#4A25E1",
        marginTop: 2,
    },
    summarySub: {
        fontSize: 8,
        color: "#9ca3af",
        marginTop: 2,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: "bold",
        marginTop: 4,
        marginBottom: 8,
        color: "#111827",
    },
    questionCard: {
        marginBottom: 12,
        padding: 12,
        backgroundColor: "#f9fafb",
        borderRadius: 6,
        borderWidth: 1,
        borderColor: "#e5e7eb",
    },
    questionHeader: {
        flexDirection: "row",
        marginBottom: 8,
    },
    questionNumber: {
        fontSize: 10,
        fontWeight: "bold",
        color: "#ffffff",
        backgroundColor: "#4A25E1",
        paddingHorizontal: 6,
        paddingVertical: 3,
        borderRadius: 4,
        marginRight: 8,
    },
    questionText: {
        flex: 1,
    },
    questionTh: {
        fontSize: 11,
        fontWeight: "bold",
        color: "#111827",
    },
    questionEn: {
        fontSize: 9,
        color: "#6b7280",
        marginTop: 2,
    },
    metaRow: {
        flexDirection: "row",
        marginTop: 6,
    },
    chip: {
        fontSize: 8,
        backgroundColor: "#ede9fe",
        color: "#5b21b6",
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 8,
        marginRight: 6,
        fontWeight: "bold",
    },
    metaText: {
        fontSize: 9,
        color: "#6b7280",
    },
    barRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 5,
    },
    barLabel: {
        width: "32%",
        fontSize: 9,
        color: "#374151",
        paddingRight: 6,
    },
    barTrack: {
        flex: 1,
        height: 10,
        backgroundColor: "#e5e7eb",
        borderRadius: 4,
        overflow: "hidden",
    },
    barFill: {
        height: 10,
        borderRadius: 4,
    },
    barValue: {
        width: 56,
        fontSize: 9,
        textAlign: "right",
        color: "#111827",
        paddingLeft: 6,
        fontWeight: "bold",
    },
    correctBadge: {
        fontSize: 8,
        color: "#047857",
        marginLeft: 4,
        fontWeight: "bold",
    },
    correctSummary: {
        marginTop: 6,
        fontSize: 9,
        color: "#047857",
        fontWeight: "bold",
    },
    textStats: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginBottom: 6,
    },
    textStatItem: {
        fontSize: 9,
        color: "#475569",
        marginRight: 14,
    },
    sampleHeader: {
        fontSize: 9,
        fontWeight: "bold",
        color: "#374151",
        marginTop: 4,
        marginBottom: 3,
    },
    sampleItem: {
        fontSize: 9,
        color: "#374151",
        marginBottom: 3,
        paddingLeft: 8,
    },
    emptyText: {
        fontSize: 9,
        color: "#94a3b8",
        fontStyle: "italic",
    },
    footer: {
        position: "absolute",
        left: 36,
        right: 36,
        bottom: 18,
        fontSize: 8,
        color: "#9ca3af",
        flexDirection: "row",
        justifyContent: "space-between",
        borderTopWidth: 1,
        borderTopColor: "#e5e7eb",
        paddingTop: 6,
    },
});

export interface QuestionPdfRow {
    question: Question;
    /** จำนวนคนที่ตอบข้อนี้ */
    answered: number;
    /** จำนวนรายงานทั้งหมดที่นำมาวิเคราะห์ */
    totalEvaluated: number;
    /** สำหรับ MC/Checkbox: count ต่อ choice index */
    choiceCounts?: number[];
    /** สำหรับ MULTIPLE_CHOICE ที่มีเฉลย */
    correctCount?: number;
    /** สำหรับ checkbox: vote รวมจากผู้ตอบทั้งหมด (คน 1 คน vote ได้หลายตัว) */
    totalVotes?: number;
    /** สำหรับ free_text/voice */
    averageCharLength?: number;
    answerSamples?: string[];
}

export interface DashboardPdfData {
    /** ชื่องาน / ชุดคำถาม สำหรับขึ้นหัว PDF */
    jobTitle: string;
    /** ชื่อบริษัท (optional) */
    companyName?: string;
    /** จำนวนผู้สมัครทั้งหมด */
    allCandidatesCount: number;
    /** จำนวนผู้สมัครที่สัมภาษณ์เสร็จ (มี report_id) */
    completedCount: number;
    /** จำนวนรายงานที่ดึงสำเร็จและนำมาวิเคราะห์จริง */
    reportsLoaded: number;
    /** เฉลี่ยจำนวนข้อที่ตอบ / คน */
    avgAnsweredPerCandidate: number;
    /** จำนวนคำถามทั้งหมดในชุด */
    totalQuestions: number;
    /** ข้อมูลแต่ละคำถาม */
    rows: QuestionPdfRow[];
    /** วัน/เวลา ที่ generate (ใช้ใส่ footer) */
    generatedAt: string;
}

function getTypeChip(question: Question, isCb: boolean): string {
    if (isCb) return "เลือกตอบ (หลายข้อ)";
    if (question.question_type === MULTIPLE_CHOICE) return "เลือกตอบ (มีเฉลย)";
    if (question.question_type === MULTIPLE_CHOICE_NO_ANSWER) return "เลือกตอบ";
    return "ข้อความ/เสียง";
}

function choiceLabel(choice: { th?: string; en?: string } | undefined, idx: number): string {
    if (!choice) return `ตัวเลือก ${idx + 1}`;
    const th = (choice.th ?? "").trim();
    const en = (choice.en ?? "").trim();
    if (th && en) return `${th} (${en})`;
    return th || en || `ตัวเลือก ${idx + 1}`;
}

const GeneralQuestionnaireDashboardPDF = ({ data }: { data: DashboardPdfData }) => (
    <Document>
        <Page size="A4" style={styles.page} wrap>
            {/* Cover header */}
            <View style={styles.coverHeader}>
                <Text style={styles.coverTitle}>สรุปผลการตอบแบบสอบถาม</Text>
                <Text style={styles.coverSubtitle}>
                    {data.jobTitle}
                    {data.companyName ? ` · ${data.companyName}` : ""}
                </Text>
            </View>

            {/* Summary cards */}
            <View style={styles.summaryGrid}>
                <View style={styles.summaryCard}>
                    <View style={styles.summaryCardInner}>
                        <Text style={styles.summaryLabel}>รายงานที่นำมาวิเคราะห์</Text>
                        <Text style={styles.summaryValue}>{data.reportsLoaded}</Text>
                        <Text style={styles.summarySub}>{`/ ${data.completedCount} เสร็จสิ้น`}</Text>
                    </View>
                </View>
                <View style={styles.summaryCard}>
                    <View style={styles.summaryCardInner}>
                        <Text style={styles.summaryLabel}>ผู้สมัครทั้งหมด</Text>
                        <Text style={styles.summaryValue}>{data.allCandidatesCount}</Text>
                    </View>
                </View>
                <View style={styles.summaryCard}>
                    <View style={styles.summaryCardInner}>
                        <Text style={styles.summaryLabel}>คำถามในชุด</Text>
                        <Text style={styles.summaryValue}>{data.totalQuestions}</Text>
                    </View>
                </View>
                <View style={styles.summaryCard}>
                    <View style={styles.summaryCardInner}>
                        <Text style={styles.summaryLabel}>เฉลี่ยข้อที่ตอบ / คน</Text>
                        <Text style={styles.summaryValue}>{data.avgAnsweredPerCandidate}</Text>
                    </View>
                </View>
            </View>

            <Text style={styles.sectionTitle}>รายละเอียดแต่ละคำถาม</Text>

            {data.rows.map((row, idx) => {
                const q = row.question;
                const isMC = isMultipleChoiceLike(q.question_type);
                const isCb = isCheckboxQuestion(q);
                // Match the on-screen denominator: percentage relative to # of people who
                // answered (or fall back to totalEvaluated to dodge division-by-zero).
                const denomForPct = Math.max(1, row.answered || row.totalEvaluated);
                const correctIdx = q.question_type === MULTIPLE_CHOICE
                    ? Number(q.answer_choice?.answer_index)
                    : -1;

                return (
                    <View style={styles.questionCard} key={q.question_id || idx} wrap={false}>
                        <View style={styles.questionHeader}>
                            <Text style={styles.questionNumber}>Q{idx + 1}</Text>
                            <View style={styles.questionText}>
                                <Text style={styles.questionTh}>{q.question.th}</Text>
                                {q.question.en ? (
                                    <Text style={styles.questionEn}>{q.question.en}</Text>
                                ) : null}
                            </View>
                        </View>

                        <View style={styles.metaRow}>
                            <Text style={styles.chip}>{getTypeChip(q, isCb)}</Text>
                            <Text style={styles.metaText}>
                                {`ตอบแล้ว ${row.answered}/${row.totalEvaluated}`}
                                {isCb && (row.totalVotes ?? 0) > 0 ? ` · ${row.totalVotes} vote` : ""}
                            </Text>
                        </View>

                        {isMC ? (
                            <View style={{ marginTop: 8 }}>
                                {(q.choices || []).map((choice, ci) => {
                                    const count = row.choiceCounts?.[ci] ?? 0;
                                    const pct = Math.round((count / denomForPct) * 100);
                                    const isCorrect = !isCb && ci === correctIdx;
                                    const fillColor = isCorrect ? "#10b981" : PALETTE[ci % PALETTE.length];
                                    return (
                                        <View style={styles.barRow} key={ci}>
                                            <Text style={styles.barLabel}>
                                                {`${ci + 1}. ${choiceLabel(choice, ci)}`}
                                                {isCorrect ? <Text style={styles.correctBadge}> ✓ เฉลย</Text> : null}
                                            </Text>
                                            <View style={styles.barTrack}>
                                                <View style={[styles.barFill, { width: `${pct}%`, backgroundColor: fillColor }]} />
                                            </View>
                                            <Text style={styles.barValue}>{`${count} (${pct}%)`}</Text>
                                        </View>
                                    );
                                })}
                                {q.question_type === MULTIPLE_CHOICE && row.answered > 0 ? (
                                    <Text style={styles.correctSummary}>
                                        {`ตอบถูก ${row.correctCount ?? 0}/${row.answered} (${Math.round(((row.correctCount ?? 0) / row.answered) * 100)}%)`}
                                    </Text>
                                ) : null}
                            </View>
                        ) : (
                            <View style={{ marginTop: 8 }}>
                                <View style={styles.textStats}>
                                    <Text style={styles.textStatItem}>
                                        {`จำนวนผู้ที่ตอบ: ${row.answered}/${row.totalEvaluated} (${Math.round((row.answered / Math.max(1, row.totalEvaluated)) * 100)}%)`}
                                    </Text>
                                    <Text style={styles.textStatItem}>
                                        {`ความยาวเฉลี่ย: ${row.averageCharLength ?? 0} ตัวอักษร`}
                                    </Text>
                                </View>
                                {(row.answerSamples?.length ?? 0) > 0 ? (
                                    <View>
                                        <Text style={styles.sampleHeader}>ตัวอย่างคำตอบ</Text>
                                        {(row.answerSamples || []).map((s, si) => (
                                            <Text style={styles.sampleItem} key={si}>{`• ${s}`}</Text>
                                        ))}
                                    </View>
                                ) : (
                                    <Text style={styles.emptyText}>ยังไม่มีข้อความตัวอย่าง</Text>
                                )}
                            </View>
                        )}
                    </View>
                );
            })}

            <View style={styles.footer} fixed>
                <Text>{`สร้างเมื่อ: ${data.generatedAt}`}</Text>
                <Text
                    render={({ pageNumber, totalPages }) => `หน้า ${pageNumber} / ${totalPages}`}
                />
            </View>
        </Page>
    </Document>
);

export default GeneralQuestionnaireDashboardPDF;
